import config from "./config.js";
import React, { useState, useEffect } from "react";
import Login from './components/Login';
import Register from './components/Register';
import Recordings from "./components/Recordings.js";
import FeatureRequests from "./components/FeatureRequests.js";
import PaymentSetup from "./components/PaymentSetup.js";
import UsageCosts from "./components/UsageCosts.js";
import LandingPage from "./components/LandingPage.js";
import LoadingScreen from './components/LoadingScreen';
import PrivacyPolicy from './components/PrivacyPolicy';
import TermsOfService from './components/TermsOfService';
import PaymentRequiredModal from './components/PaymentRequiredModal';
import PaymentBlockedModal from './components/PaymentBlockedModal';
import {
  TopNavigation,
  ButtonDropdown,
  Modal,
  Box,
  SpaceBetween,
  Button,
  FormField,
  Input,
  Alert
} from "@cloudscape-design/components";
import { applyTheme } from "@cloudscape-design/components/theming";
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { Amplify } from 'aws-amplify';
import { AuthProvider, useAuth } from './context/AuthContext';
import { CacheProvider } from './context/CacheContext';
import { LambdaClient, InvokeCommand } from '@aws-sdk/client-lambda';
import { fetchAuthSession } from 'aws-amplify/auth';
import './styles.css';

Amplify.configure({
  Auth: {
    Cognito: {
      region: config.Region,
      userPoolId: config.UserPoolId,
      userPoolClientId: config.ClientId,
      identityPoolId: config.IdentityPoolId
    }
  },
  Storage: {
    S3: {
      bucket: config.Bucket,
      region: config.Region,
    }
  }
});


const AppContent = () => {
  const { isAuthenticated, isLoading, userEmail, displayName, hasPaymentMethod, trialStatus, handleSignOut, setupPaymentMethod } = useAuth();
  const [theme, setTheme] = useState(() => localStorage.getItem('cloudscape-theme') || 'dark');
  const [showCloseAccountModal, setShowCloseAccountModal] = useState(false);
  const [closeAccountPassword, setCloseAccountPassword] = useState('');
  const [isClosingAccount, setIsClosingAccount] = useState(false);
  const [closeAccountError, setCloseAccountError] = useState('');

  const navigate = useNavigate();
  const location = window.location;

  // Store intended destination when redirected to payment-setup
  useEffect(() => {
    if (isAuthenticated && (trialStatus.needsPayment === 'blocked' || trialStatus.needsPayment === 'new_user')) {
      const currentPath = window.location.pathname;
      const publicPaths = ['/', '/privacy', '/terms', '/login', '/register'];
      const isPublicPath = publicPaths.includes(currentPath);
      
      if (!isPublicPath && currentPath !== '/payment-setup') {
        // Store where user was trying to go
        sessionStorage.setItem('recapify_redirect_after_setup', currentPath);
        console.log(`User needs payment setup (${trialStatus.needsPayment}), redirecting to /payment-setup`);
        navigate('/payment-setup');
      } else if (currentPath !== '/payment-setup') {
        // User is on a public page, redirect to payment-setup but default to /recordings after
        if (!sessionStorage.getItem('recapify_redirect_after_setup')) {
          sessionStorage.setItem('recapify_redirect_after_setup', '/recordings');
        }
        navigate('/payment-setup');
      } else {
        // User is already on payment-setup, ensure we have a redirect destination
        if (!sessionStorage.getItem('recapify_redirect_after_setup')) {
          sessionStorage.setItem('recapify_redirect_after_setup', '/recordings');
        }
      }
    }
  }, [isAuthenticated, trialStatus.needsPayment, navigate]);

  useEffect(() => {
    // Define custom theme matching landing page colors
    const customTheme = {
      tokens: {
        colorBackgroundLayoutMain: {
          light: '#f9fafb',
          dark: '#0f0f1e'
        },
        colorBackgroundContainerContent: {
          light: '#ffffff',
          dark: '#1a1a2e'
        },
        colorBackgroundContainerHeader: {
          light: '#f3f4f6',
          dark: '#16213e'
        },
        colorTextBodyDefault: {
          light: '#1f2937',
          dark: '#e0e0e0'
        },
        colorTextHeadingDefault: {
          light: '#1f2937',
          dark: '#ffffff'
        },
        colorTextBodySecondary: {
          light: '#6b7280',
          dark: '#b0b0b0'
        },
        colorBackgroundButtonPrimaryDefault: '#667eea',
        colorBackgroundButtonPrimaryHover: '#764ba2',
        colorBackgroundButtonPrimaryActive: '#5a67d8',
        colorBorderDividerDefault: {
          light: 'rgba(59, 130, 246, 0.2)',
          dark: 'rgba(102, 126, 234, 0.2)'
        },
        colorBorderContainerTop: {
          light: 'rgba(59, 130, 246, 0.3)',
          dark: 'rgba(102, 126, 234, 0.3)'
        },
        colorBackgroundInputDefault: {
          light: '#ffffff',
          dark: '#1e1e3f'
        },
        colorBorderInputDefault: {
          light: 'rgba(59, 130, 246, 0.2)',
          dark: 'rgba(102, 126, 234, 0.3)'
        }
      }
    };

    // Apply theme using Cloudscape's runtime theming API
    const { reset } = applyTheme({ theme: customTheme });
    
    // Apply theme mode to body element
    document.body.classList.remove('awsui-dark-mode', 'awsui-light-mode');
    if (theme === 'dark') {
      document.body.classList.add('awsui-dark-mode');
    }
    
    localStorage.setItem('cloudscape-theme', theme);

    // Cleanup function to reset theme on unmount
    return () => reset();
  }, [theme]);

  const toggleTheme = () => {
    setTheme(prevTheme => prevTheme === 'light' ? 'dark' : 'light');
  };

  const handleMenuItemClick = ({ detail }) => {
    if (detail.id === "login") {
      navigate('/login');
    }

    if (detail.id === "register") {
      navigate('/register');
    }

    if (detail.id === "recordings") {
      navigate('/recordings');
    }

    if (detail.id === "feature-requests") {
      navigate('/feature-requests');
    }

    if (detail.id === "signout") {
      handleSignOut();
    }

    if (detail.id === "close-account") {
      setShowCloseAccountModal(true);
    }
  };

  const handleCloseAccount = async () => {
    setIsClosingAccount(true);
    setCloseAccountError('');
    
    try {
      const { credentials } = await fetchAuthSession();
      
      const lambda = new LambdaClient({ 
        region: config.Region,
        credentials: credentials
      });
      
      const command = new InvokeCommand({
        FunctionName: config.CloseAccountFunctionArn,
        Payload: new TextEncoder().encode(JSON.stringify({
          action: 'close_account',
          password: closeAccountPassword
        }))
      });
      
      const response = await lambda.send(command);
      const result = JSON.parse(new TextDecoder().decode(response.Payload));
      
      console.log('Lambda response:', result);
      
      if (result.statusCode === 200) {
        setShowCloseAccountModal(false);
        alert('Your account has been closed. You will be logged out.');
        await handleSignOut();
        navigate('/');
      } else if (result.statusCode === 401) {
        setCloseAccountError('Invalid password. Please try again.');
      } else {
        setCloseAccountError('Failed to close account. Please try again.');
      }
    } catch (err) {
      console.error('Error closing account:', err);
      setCloseAccountError('Error: ' + err.message);
    } finally {
      setIsClosingAccount(false);
    }
  };

  return (
    <>
      <div className="sticky-nav">
        <TopNavigation
        identity={{
          href: "/",
          title: "Recapify",
          logo: {
            src: "/icon.png",
            alt: "MM-Icon"
          }
        }}
        utilities={[
          ...(isAuthenticated ? [
            {
              type: "button",
              text: "Recordings",
              onClick: () => navigate('/recordings')
            },
            {
              type: "button", 
              text: "Usage & Costs",
              onClick: () => navigate('/financials')
            },
            {
              type: "button", 
              text: "Feature Requests",
              onClick: () => navigate('/feature-requests')
            }
          ] : []),
          {
            type: "button",
            text: theme === 'dark' ? '☀️' : '🌙',
            ariaLabel: "Toggle dark mode",
            onClick: toggleTheme,
            title: theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'
          },
          {
            type: "menu-dropdown",
            text: isAuthenticated ? (displayName || "User") : "Sign In",
            description: isAuthenticated ? (displayName || "") : "",
            iconName: "user-profile",
            items: isAuthenticated ? [
              { id: "signout", text: "Sign out" },
              { id: "close-account", text: "Close Account" }
            ] : [
              { id: "login", text: "Login" },
              { id: "register", text: "Register" },
            ],
            onItemClick: handleMenuItemClick
          }
        ]}
      />
      </div>
      
      {isAuthenticated && <PaymentRequiredModal />}
      {isAuthenticated && <PaymentBlockedModal />}
      
      <Modal
        visible={showCloseAccountModal}
        onDismiss={() => {
          setShowCloseAccountModal(false);
          setCloseAccountPassword('');
          setCloseAccountError('');
        }}
        size="large"
        header={<div style={{ textAlign: 'center', width: '100%' }}>Close Account</div>}
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button
                variant="link"
                onClick={() => {
                  setShowCloseAccountModal(false);
                  setCloseAccountPassword('');
                  setCloseAccountError('');
                }}
                disabled={isClosingAccount}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleCloseAccount}
                disabled={!closeAccountPassword || isClosingAccount}
                loading={isClosingAccount}
              >
                Yes, Close My Account
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        <SpaceBetween size="l">
          <Alert type="warning" header="This action cannot be undone" statusIconAriaLabel="Warning">
            <SpaceBetween size="xs">
              <Box></Box>
              <Box>• Your account will be closed immediately</Box>
              <Box>• All access will be revoked</Box>
              <Box>• All recordings, transcriptions, and summaries will be permanently deleted</Box>
              <Box>• Your data cannot be recovered</Box>
              <Box>• A final invoice will be sent within 24 hours</Box>
            </SpaceBetween>
          </Alert>

          {closeAccountError && (
            <Alert type="error" header="Error">
              {closeAccountError}
            </Alert>
          )}

          <FormField
            label="Enter your password to confirm"
            description="This action requires password verification"
          >
            <Input
              type="password"
              value={closeAccountPassword}
              onChange={({ detail }) => setCloseAccountPassword(detail.value)}
              placeholder="Password"
              disabled={isClosingAccount}
            />
          </FormField>
        </SpaceBetween>
      </Modal>
      
      <Routes>
        <Route path="/login" element={!isAuthenticated ? <Login /> : <Navigate to="/recordings" />} />
        <Route path="/register" element={!isAuthenticated ? <Register /> : <Navigate to="/recordings" />} />
        <Route 
          path="/payment-setup" 
          element={
            isLoading ? (
              <LoadingScreen message="Loading payment setup" />
            ) : isAuthenticated ? (
              <PaymentSetup onPaymentSetup={setupPaymentMethod} />
            ) : (
              <Navigate to="/login" />
            )
          } 
        />
        <Route 
          path="/recordings" 
          element={
            isLoading ? (
              <LoadingScreen message="Preparing your dashboard" />
            ) : isAuthenticated ? (
              (trialStatus.needsPayment !== 'blocked' && trialStatus.needsPayment !== 'new_user') ? (
                <Recordings />
              ) : (
                <Navigate to="/payment-setup" />
              )
            ) : (
              <Navigate to="/login" />
            )
          } 
        />
        <Route 
          path="/feature-requests" 
          element={
            isLoading ? (
              <LoadingScreen message="Loading feature requests" />
            ) : isAuthenticated ? (
              (trialStatus.needsPayment !== 'blocked' && trialStatus.needsPayment !== 'new_user') ? (
                <FeatureRequests />
              ) : (
                <Navigate to="/payment-setup" />
              )
            ) : (
              <Navigate to="/login" />
            )
          } 
        />
        <Route 
          path="/financials" 
          element={
            isLoading ? (
              <LoadingScreen message="Loading usage data" />
            ) : isAuthenticated ? (
              (trialStatus.needsPayment !== 'blocked' && trialStatus.needsPayment !== 'new_user') ? (
                <UsageCosts />
              ) : (
                <Navigate to="/payment-setup" />
              )
            ) : (
              <Navigate to="/login" />
            )
          } 
        />
        <Route path="/" element={<LandingPage theme={theme} />} />
        <Route path="/privacy" element={<PrivacyPolicy theme={theme} />} />
        <Route path="/terms" element={<TermsOfService theme={theme} />} />
        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
    </>
  );
};

export default function App() {
  return (
    <AuthProvider>
      <CacheProvider>
        <BrowserRouter>
          <AppContent />
        </BrowserRouter>
      </CacheProvider>
    </AuthProvider>
  );
}
